import * as Actions from '../actions/participantActions';
import Participant from '../../entities/participant';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {getSessionFirmId, getSessionUserId} from '../../lib/picos/sessionIdentity';
import {isRecoverableError} from '../../lib/recoverableErrors';
import * as Pending from '../../lib/pendingRows';

export type ParticipantAction = {
    type: string;
    participant: Participant | null; 
    participantId: string | Participant; 
    partial: boolean; 
    lastFetch: number; 
    participants: Participant[]; 
    err: any; 
    status: number; 
    message: string;
    sentAddedOn?: number;
    // OWA-1018: this tab's own saves (see lib/pendingRows)
    dispatchedAddedOn?: number;
    dispatchedAddedOns?: number[];
    sentAddedOns?: (number | undefined)[];
    queuedAt?: number;
    current?: Participant | null;
    keep?: boolean;
    live?: number[];
    refused?: number[];
    readAt?: number;
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<Participant> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {Participant[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<Participant> | null, firmId: string, userId: string, updates?: Participant[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'participant', 'participants', data ? backupRows(data) : null, updates)
		  .then(result => {
			  // OWA-786 (CXR-4-001): the worker resolves a typed result for every
			  // path — a resolution is NOT an acknowledgement. Only ok: true means
			  // the backup write is durable; anything else is surfaced, never
			  // swallowed, so a skipped or failed write cannot masquerade as one.
			  if (!result?.ok) console.warn(`⮂ participantReducer: backup write for 'participants' did not persist (${result?.reason ?? 'unknown'}): ${result?.message ?? ''}`);
		  })
		  .catch(console.error);
}

/**
 * The identity that names this tab's backup store, or null when this tab has
 * not finished authenticating.
 *
 * OWA-785: this must come from sessionStorage (per tab), never from
 * localStorage (shared by every tab on the origin). With two firms open in two
 * tabs, the localStorage value is whichever firm logged in last, so the other
 * tab persisted its entities into that firm's backup store and they restored
 * into the wrong firm's UI. A partial identity names some other store, so we
 * skip the write rather than misfile it.
 *
 * @returns {{firmId: string, userId: string} | null}
 */
function storageIdentity(): {firmId: string; userId: string} | null {
	const firmId = getSessionFirmId();
	const userId = getSessionUserId();

	if (!firmId || !userId) {
		if (logging) console.debug('⮂ participantReducer: no session identity, skipping backup write');
		return null;
	}

	return {firmId, userId};
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<Participant>} data
 * @param {Participant[]} [updates]
 * @returns {ArrayMap<Participant>}
 */
function updateStorage(data: ArrayMap<Participant>, updates?: Participant[]): ArrayMap<Participant> {
	if (logging) console.debug('⮂ participantReducer: updateStorage');

	if (updates && updates.length === 0) return data;

	const identity = storageIdentity();
	if (!identity) return data;

	if (updates) callStorageWorker(null, identity.firmId, identity.userId, updates);
	else callStorageWorker(data, identity.firmId, identity.userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {Participant[]} [updates]
 * @returns {ArrayMap<Participant>}
 */
function updateStorageNoReturn(updates?: Participant[]): void {
	if (logging) console.debug('⮂ participantReducer: updateStorageNoReturn');

	if (!updates || updates.length === 0) return;

	const identity = storageIdentity();
	if (!identity) return;

	callStorageWorker(null, identity.firmId, identity.userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {Participant[]} [data]
 * @returns {ArrayMap<Participant>}
 */
function createState(data?: Participant[] | ArrayMap<Participant>): ArrayMap<Participant> {
	if (!data) return ArrayMapRegistry.clone<Participant>('participant', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<Participant>('participant', data.map(e => e));
    else return ArrayMapRegistry.clone<Participant>('participant', data);
}

// Whether deleted participants keep their rows (schema includeDeletedInResults). OWA-68
const retainDeleted = false;

/**
 * The full backup snapshot of a collection: one entry per ID. A row when there is one (deleted rows of a
 * type that keeps them included); otherwise a deletion record for an ID whose row was removed. OWA-68
 *
 * @param {ArrayMap<Participant>} data
 * @returns {Array<Participant | ParticipantDeletionRecord>}
 */
function backupRows(data: ArrayMap<Participant>): Array<Participant | ParticipantDeletionRecord> {
	const records: ParticipantDeletionRecord[] = data.deletedVersionEntries()
		.filter(([id]) => !data.has(id))
		.map(([id, addedOn]) => ({participantId: id, addedOn, status: 'D', deletionRecord: true}));

	return records.length ? [...data.values(), ...records] : data.values();
}

/**
 * A deletion record read back from the offline backup: evidence that a participant was deleted at a
 * version, never a live row. The storage worker writes one for a deleted participant whose type does
 * not keep deleted rows. OWA-68
 */
type ParticipantDeletionRecord = {participantId: string; addedOn: number; status: 'D'; deletionRecord: true};

const isDeletionRecord = (o: unknown): o is ParticipantDeletionRecord => (o as ParticipantDeletionRecord)?.deletionRecord === true;

/**
 * Apply one incoming version of a participant to a collection the caller owns (a clone), under the one
 * acceptance rule (OWA-68). A matching write acknowledgement may replace its own optimistic row (OWA-1375):
 * - older than the stored row: rejected
 * - an active version equal to a kept deleted row: rejected (it yields only to a strictly newer one)
 * - no stored row, and at or below the recorded deletion version: rejected, unless the deletion version is the queued version this answer names (the refetch after a refused delete, OWA-1692)
 * - an accepted deleted version records its deletion version; the row is kept as a deleted row when
 *   this type keeps deleted rows, otherwise it is removed
 * - an accepted active version is stored (a newer one supersedes the deletion version)
 *
 * @param {ArrayMap<Participant>} st
 * @param {Participant | ParticipantDeletionRecord} incoming
 * @param {number} [sentAddedOn] the queued version this answer acknowledges
 * @returns {boolean} whether the version was accepted; only accepted versions go to the backup
 */
function applyAccepted(st: ArrayMap<Participant>, incoming: Participant | ParticipantDeletionRecord, sentAddedOn?: number): boolean {
	const id = isDeletionRecord(incoming) ? incoming.participantId : incoming.id;
	const current = st.get(id);

	if (current) {
		if (incoming.addedOn < current.addedOn && current.addedOn !== sentAddedOn) return false;
		// A kept deleted row yields only to a strictly newer active version, like a deletion version
		if (current.status === 'D' && incoming.status !== 'D' && incoming.addedOn === current.addedOn && current.addedOn !== sentAddedOn) return false;
	}
	else {
		const deletedAt = st.deletedVersion(id);
		if (deletedAt !== undefined && incoming.addedOn <= deletedAt && deletedAt !== sentAddedOn) return false;
		// The refused delete is replaced, so its deletion version no longer stands
		if (deletedAt !== undefined && deletedAt === sentAddedOn && incoming.status !== 'D') st.clearDeletion(id);
	}

	if (incoming.status === 'D') {
		st.recordDeletion(id, incoming.addedOn);
		if (retainDeleted && !isDeletionRecord(incoming)) st.set(incoming);
		else if (current) st.delete(current);
	}
	else st.set(incoming as Participant);

	return true;
}

/**
 * Apply one incoming version (a fetch, a socket message, the backup) under the one acceptance rule.
 * A row this tab is still saving is never replaced: the version is kept as the record's latest
 * accepted version, which the row returns to if the save is not accepted (OWA-1018). Such a version
 * is not written to the backup.
 *
 * @param {ArrayMap<Participant>} st
 * @param {Participant | ParticipantDeletionRecord} incoming
 * @param {number} [sentAddedOn] the queued version this answer acknowledges
 * @returns {boolean} whether the version was accepted into the rows shown
 */
function applyVersion(st: ArrayMap<Participant>, incoming: Participant | ParticipantDeletionRecord, sentAddedOn?: number): boolean {
	const id = isDeletionRecord(incoming) ? incoming.participantId : incoming.id;
	if (Pending.keepBehindPending(st, id, incoming)) return false;
	return applyAccepted(st, incoming, sentAddedOn);
}

/**
 * Write what settling a pending save leaves for the backup (OWA-1018): the version now shown, which
 * replaces the queued row there, or the whole collection when a record this tab created is gone.
 *
 * @param {ArrayMap<Participant>} st
 * @param {Pending.BackupWrite | null} write
 * @returns {ArrayMap<Participant>}
 */
function backupSettled(st: ArrayMap<Participant>, write: Pending.BackupWrite | null): ArrayMap<Participant> {
	if (write?.full) return updateStorage(st);
	return write?.rows.length ? updateStorage(st, write.rows) : st;
}

/**
 * A new indexed collection holding the rows, deletion versions and pending saves of the given one.
 * Fetch paths build from this instead of cloning, because the initial store state is a bare ArrayMap
 * without the schema's indexes. OWA-68, OWA-1018
 *
 * @param {ArrayMap<Participant>} from
 * @returns {ArrayMap<Participant>}
 */
function carryOver(from: ArrayMap<Participant>): ArrayMap<Participant> {
	const next = createState(from);
	from.deletedVersionEntries().forEach(([id, version]) => next.recordDeletion(id, version));
	from.pendingSaveEntries().forEach(([id, entry]) => next.setPendingSave(id, entry));
	return next;
}

function participants(state: ArrayMap<Participant>, action: ParticipantAction): ArrayMap<Participant> {
	if (!state) state = createState();

	let st: ArrayMap<Participant>;

	switch (action.type) {
		// action.participant - the new value of participant with id action.id
		// action.participantId - id of the target, used if new value is null
		case Actions.PARTICIPANT_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ participantReducer: FETCH_ONE_SUCCESS', action);

			if (action.participant === null) {
				// A release fetch can confirm that a retained create never reached the server.
				// Its absence cannot erase a newer local edit.
				const row = state.get(action.participantId as string);
				if (action.sentAddedOn === undefined || !row || row.addedOn !== action.sentAddedOn || state.pendingSave(row.id)) return state;
				st = state.clone();
				st.delete(row);
				return updateStorage(st);
			}

			// Only for a participant this collection knows (a row, or a deletion version), and only when the
			// fetched version is accepted (OWA-68).
			if (state.has(action.participantId) || state.deletedVersion(action.participant.id) !== undefined) {
				st = state.clone();
				// Behind a pending row of this tab the version is kept as the latest accepted one (OWA-1018)
				if (!applyVersion(st, action.participant, action.sentAddedOn)) return st.pendingSave(action.participant.id) ? st : state;

				updateStorageNoReturn([action.sentAddedOn === undefined ? action.participant : Object.assign({}, action.participant, {staleReplaces: action.sentAddedOn})]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.participants
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.participants
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.participants
			 !partial, !stateEmpty, fetchEmpty	-> D return participants in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge participants in state newer than those in action.participants
		 */
		case Actions.PARTICIPANT_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.participants.length === 0;
			// A deletion record from the backup is evidence only: never constructed into an entity (OWA-68)
			const fetched: Array<Participant | ParticipantDeletionRecord> = action.participants.map(participant => isDeletionRecord(participant) ? participant : new Participant({...participant, persisted: true}));
			const fetchedIds = new Set(fetched.map(participant => isDeletionRecord(participant) ? participant.participantId : participant.id));

			// A full fetch is the whole dataset, so a persisted active row it omits is dropped (deleted rows,
			// deletion versions, unsaved local rows and rows this tab is still saving stay). OWA-68, OWA-1018
			const dropOmitted = (st: ArrayMap<Participant>) => {
				[...st.values()].forEach(row => {
					if (!fetchedIds.has(row.id) && row.persisted && row.status !== 'D' && !st.pendingSave(row.id)) st.delete(row);
				});
			};

			if (logging) console.debug(`⮂ participantReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} participants with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ participantReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Build a new indexed state that keeps any deletion versions (the visible state is
					// empty, but a deletion version can still reject an older row). OWA-68
					st = carryOver(state);
					fetched.forEach(participant => applyVersion(st, participant));

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ participantReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<Participant> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: Participant[] = [];

				// Partials include deleted rows for every entity; applyVersion keeps or removes them and
				// records their deletion version (OWA-68). Only accepted versions go to the backup.
				fetched.forEach(participant => {
					if (applyVersion(st, participant)) toUpdate.push(participant as Participant);
					else if (logging) console.debug('⮂ participantReducer: partialMerge: rejected older version of participant');
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ participantReducer: empty: participants', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Drop the persisted active rows; deleted rows, deletion versions and unsaved local rows
				// stay (OWA-68)
				st = carryOver(state);
				dropOmitted(st);

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ participantReducer: fullMerge: participants', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Start from the current rows and deletion versions, apply each fetched version under the one
				// rule, then drop persisted active rows the full fetch omitted (OWA-68). A fetched row replaces
				// a local one unless the local one is strictly newer, so a stale local (persisted or not) never
				// shadows a newer server row (OWA-7 #1), and a newer row that arrived while the fetch was in
				// flight (for example a socket update during startup restore) is kept.
				st = carryOver(state);
				fetched.forEach(participant => applyVersion(st, participant));
				dropOmitted(st);

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.PARTICIPANT_FETCH_ONE_FAILURE:
			console.error('PARTICIPANT_FETCH_ONE_FAILURE', action.participantId, action.err);

			// No update to storage required
			return state;

		case Actions.PARTICIPANT_FETCH_FAILURE:
			console.error('PARTICIPANT_FETCH_FAILURE', action.participantId, action.err);

			// No update to storage required
			return state;

		case Actions.PARTICIPANT_RESTORE: {
		    let participant = new Participant({...action.participant});
            st = state.clone();

            // One acceptance rule for every incoming version (OWA-68). This tab's own restore supersedes
            // its pending save of the record (OWA-1018).
            Pending.supersedePending(st, participant.id);
            if (!applyVersion(st, participant)) return state;

            return updateStorage(st, [participant]);
        }
            
		case Actions.PARTICIPANT_SAVE_QUEUE: {
			// OWA-1018: show the save at once, marked pending, before its saga waits to queue it, so a save
			// cloned from the store in the same tick carries this one's changes. OWA-1083: it is stamped
			// after the version this tab holds; the dispatched object takes the stamp, so the saga queues it.
			// Stamping here (it moves the tab's counter and writes the dispatched object) is deliberate: the
			// saga runs after this reducer and must queue the stamp the store shows.
			if (!action.participant) return state;
			let participant = new Participant({...action.participant});
			Pending.stampAfterHeld(state, participant);
			action.participant.addedOn = participant.addedOn;

			st = state.clone();
			// The backup takes the row once it is queued (PARTICIPANT_SAVE)
			return Pending.beginPending(st, participant, applyAccepted) ? st : state;
		}

		case Actions.PARTICIPANT_SAVE_MANY_QUEUE: {
			// OWA-1018, OWA-1083: as PARTICIPANT_SAVE_QUEUE, for each row in dispatch order
			if (!Array.isArray(action.participants)) return state;
			st = state.clone();
			let shown = false;
			action.participants.forEach(dispatched => {
				if (!dispatched) return;
				const participant = new Participant({...dispatched});
				Pending.stampAfterHeld(st, participant);
				dispatched.addedOn = participant.addedOn;
				shown = Pending.beginPending(st, participant, applyAccepted) || shown;
			});
			return shown ? st : state;
		}

		case Actions.PARTICIPANT_REMOVE_QUEUE:
		case Actions.PARTICIPANT_RESTORE_QUEUE:
			// OWA-1083: the saga stamps a remove or a restore; raise the clock so that stamp follows the
			// version this tab holds
			if (action.participant?.id) Pending.raiseClockToHeld(state, action.participant.id);
			return state;

		case Actions.PARTICIPANT_SAVE: {
		    // console.debug('⮂ PARTICIPANT_SAVE reducer', action.participant?.id);
		    
			// clone the participant
			let participant = new Participant({...action.participant});

			st = state.clone();
			// state.clear(); 
			
			
			// OWA-1018: the queued copy of this tab's pending save replaces the row shown at dispatch (the
			// queue may have stamped it again). A later save of this tab keeps its row.
			if (action.dispatchedAddedOn !== undefined) {
				const shown = Pending.showQueued(st, participant, action.dispatchedAddedOn, action.queuedAt, applyAccepted);
				if (shown === 'superseded' || (shown === 'unmarked' && !applyVersion(st, participant))) return state;
				return updateStorage(st, [participant]);
			}

			// Add or update the new participant in the state, under the one acceptance rule (OWA-68). A rejected
			// version changes nothing and is not written to the backup. A local write that did not come
			// through the queue supersedes this tab's pending save of the record (OWA-1018).
			Pending.supersedePending(st, participant.id);
			if (!applyVersion(st, participant)) return state;

			return updateStorage(st, [participant]);
		}

		case Actions.PARTICIPANT_SAVE_MANY: {
		    if (logging) console.debug('⮂ PARTICIPANT_SAVE_MANY reducer', action.participants.map(participant => participant.id).join(','));
		    
			// clone the participants
			let toProcess = action.participants.map(participant => new Participant(participant));

			st = state.clone();
			// state.clear();

			const updates: Participant[] = [];

			toProcess.forEach((participant, i) => {
				// OWA-1018: a row this tab queued replaces the row shown at dispatch (see PARTICIPANT_SAVE)
				const dispatchedAddedOn = action.dispatchedAddedOns?.[i];
				if (dispatchedAddedOn !== undefined) {
					const shown = Pending.showQueued(st, participant, dispatchedAddedOn, action.queuedAt, applyAccepted);
					if (shown === 'shown' || (shown === 'unmarked' && applyVersion(st, participant))) updates.push(participant);
				}
				// Add or update the new participant in the state, under the one acceptance rule (OWA-68)
				else if (applyVersion(st, participant)) updates.push(participant);
				else if (logging) console.debug('⮂ PARTICIPANT_SAVE_MANY: rejected older version of participant');
			});

			return updateStorage(st, updates);
		}

		case Actions.PARTICIPANT_REMOVE: {
			// clone the participant; a remove is a deleted version (the remove saga stamps it 'D' and a new
			// addedOn)
			const participant = new Participant({...action.participant, status: 'D'});

			// Remove from UI under the one acceptance rule: the deletion version is recorded, and the row
			// is removed, or kept as a deleted row for a type that keeps them (OWA-68). This tab's own
			// remove supersedes its pending save of the record (OWA-1018).
			st = state.clone();
			Pending.supersedePending(st, participant.id);
			if (!applyVersion(st, participant)) return state;

			return updateStorage(st, [participant]);
		}

		case Actions.PARTICIPANT_RESTORE_FAILURE:
		case Actions.PARTICIPANT_SAVE_FAILURE: {
		    console.warn('PARTICIPANT_SAVE_FAILURE reducer', action.participant?.id);
		    if (!action.participant?.id) {
				console.warn('PARTICIPANT_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const participant = ensure(Participant, action.participant);
			if (!participant) {
				console.error('Could not find participant after call to save');
				return state;
			}

			st = state.clone();

			const _participantRef = state.get(participant.id);

			if (_participantRef && (_participantRef.addedOn === participant.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _participant = new Participant({..._participantRef});
				_participant.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_participant.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.update('participant', participant.addedOn, {fatalError: _participant.fatalError}).then(r => {
						if (r.isErr()) console.error(`⮂ participantReducer: failed to save participant with fatalError info: ${r.unwrapErr().message}`);
						else if (r.unwrap() === 0) {
							// Non-document 4xx handlers may remove the row before dispatch.
							// Preserve the historical rejected-operation queue behavior.
							idb.add('participant', _participant).then(saved => {
								if (saved.isErr()) console.error('Could not restore rejected participant: ' + saved.unwrapErr().message);
							}).catch(err => console.error('Could not restore rejected participant: ' + err.message));
						}
						else console.debug('⮂ participantReducer: saved participant with fatalError info');
					}).catch(err => console.error(`⮂ participantReducer: failed to save participant with fatalError info: ${err.message}`));

				// OWA-1018: a refusal the server gives for good (a 409, a held document status, or a terminal
				// 4xx such as 403, 404 or 422) settles this save's pending mark: the row returns to the latest
				// accepted version. A document keeps its row, which may hold the only copy of the user's work.
				const refusedForGood = Pending.isTerminalRefusal('participant', action.status) && st.pendingSave(participant.id)?.key === participant.addedOn;
				if (refusedForGood && !Pending.keepsRefusedRows('participant')) Pending.settlePending(st, participant.id, participant.addedOn, {kind: 'refused'}, applyAccepted);
				else {
					if (refusedForGood) Pending.supersedePending(st, participant.id);
					st.set(_participant);
				}
			}

			return updateStorage(st);
		}

		case Actions.PARTICIPANT_SAVE_MANY_FAILURE: {
		    console.warn('PARTICIPANT_SAVE_MANY_FAILURE reducer', action.participants.map(participant => participant.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.PARTICIPANT_RESTORE_SUCCESS:
		case Actions.PARTICIPANT_SAVE_SUCCESS: {
			if (!action.participant?.id) {
				console.warn('PARTICIPANT_SAVE_SUCCESS reducer');
                return state;
			}

			const participantRef = state.get(action.participant?.id);

			// OWA-1018: the answer settles its own operation's pending mark (the record shows the answer, or
			// a newer accepted version). An earlier operation's answer is kept behind a later save's row.
			if (action.sentAddedOn !== undefined && state.pendingSave(action.participant.id)) {
				st = state.clone();
				const settled = Pending.settlePending(st, action.participant.id, action.sentAddedOn, {kind: 'accepted', answer: action.participant}, applyAccepted);
				if (settled) return backupSettled(st, settled);
				Pending.keepBehindPending(st, action.participant.id, action.participant);
				return st;
			}

			// OWA-1375: this answer settles only the optimistic version that was sent.
			// A later local edit must await its own acknowledgement, even if the server restamps this one.
			// A version the server accepted (a later save that was refused returned the row to it) takes
			// the answer under the acceptance rule (OWA-1018).
			if (action.sentAddedOn !== undefined && participantRef && participantRef.addedOn !== action.sentAddedOn && !participantRef.persisted) return state;

			st = state.clone();
			if (!applyVersion(st, action.participant, action.sentAddedOn)) return state;

			// The backup also rejects older versions unless it is replacing this exact queued write.
			return updateStorage(st, [action.sentAddedOn === undefined
				? action.participant
				: Object.assign({}, action.participant, {staleReplaces: action.sentAddedOn})]);
		}

		case Actions.PARTICIPANT_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ PARTICIPANT_SAVE_MANY_SUCCESS reducer', action.participants.map(participant => participant.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const participants: Participant[] = [];
			let rewrite = false;

			action.participants.forEach((e, i) => {
				const participantRef = st.get(e.id);
				// OWA-1375: the queued version this answer acknowledges, as in SAVE_SUCCESS
				const sentAddedOn = action.sentAddedOns?.[i];
				const settled = sentAddedOn !== undefined && participantRef
					? Pending.settlePending(st, e.id, sentAddedOn, {kind: 'accepted', answer: e}, applyAccepted)
					: null;

                // Missing rows still use the version/deletion acceptance rule.
				// OWA-1018: the answer settled its own operation's pending mark (see PARTICIPANT_SAVE_SUCCESS)
				if (settled) {
					participants.push(...settled.rows as Participant[]);
					rewrite ||= settled.full;
				}
				// OWA-1018: behind a later save of this tab, the echoed version is kept as the latest
				// accepted version under the one acceptance rule (OWA-68)
				else if (st.pendingSave(e.id)) {
					if (applyVersion(st, e)) participants.push(e);
				}
				// A pending local edit awaits its own acknowledgement (OWA-1375)
				else if (sentAddedOn !== undefined && participantRef && participantRef.addedOn !== sentAddedOn && !participantRef.persisted) return;
				// The answer goes through the one acceptance rule (OWA-68); it replaces its own optimistic row
				else if (applyVersion(st, e, sentAddedOn)) participants.push(sentAddedOn === undefined ? e : Object.assign({}, e, {staleReplaces: sentAddedOn}));
				else if (sentAddedOn === undefined && participantRef && !participantRef.persisted) {
					// Older echo: only the current row's persisted flag changes, and the current row is
					// what the backup receives. Create a new instance to avoid mutating the entity shared
					// with old state.
					const participant = new Participant({...participantRef, persisted: true});
					st.set(participant);
					participants.push(participant);
				}
			});

			return rewrite ? updateStorage(st) : updateStorage(st, participants);
		}

		case Actions.PARTICIPANT_REMOVE_FAILURE: {
		    console.warn('PARTICIPANT_REMOVE_FAILURE action', action.participant?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.PARTICIPANT_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.PARTICIPANT_RESTORE_MANY: {
			if (logging) console.debug('⮂ PARTICIPANT_RESTORE_MANY reducer', action.participants.map(participant => participant.id).join(','));

			const toProcess = action.participants.map(participant => new Participant({...participant}));
			st = state.clone();

			const updates: Participant[] = [];

			// One acceptance rule for every incoming version (OWA-68): a deleted version removes the row and
			// records its deletion version, or keeps a deleted row for a type that keeps them.
			toProcess.forEach(participant => {
				if (applyVersion(st, participant)) updates.push(participant);
				else if (logging) console.debug('⮂ PARTICIPANT_RESTORE_MANY: rejected older version of participant');
			});

			return updateStorage(st, updates);
		}

		case Actions.PARTICIPANT_REMOVE_MANY: {
			if (logging) console.debug('⮂ PARTICIPANT_REMOVE_MANY reducer', action.participants.map(participant => participant.id).join(','));

			// A socket delete is a deleted version, under the one acceptance rule (OWA-68)
			const toProcess = action.participants.map(participant => new Participant({...participant, status: 'D'}));
			st = state.clone();

			const updates: Participant[] = [];

			toProcess.forEach(participant => {
				if (applyVersion(st, participant)) updates.push(participant);
				else if (logging) console.debug('⮂ PARTICIPANT_REMOVE_MANY: rejected older version of participant');
			});

			return updateStorage(st, updates);
		}

		case Actions.PARTICIPANT_SAVE_REVERT: {
			// OWA-1018: this save will not be accepted (it was refused, or never reached the queue). Its
			// pending mark is settled and the row returns to the latest accepted version, or to `current`
			// (the record as the server holds it) when that is newer. `keep` leaves the row shown instead.
			if (!action.participant?.id) return state;
			const id = action.participant.id;
			const key = action.sentAddedOn ?? action.participant.addedOn;
			const current = action.current ? new Participant({...action.current, persisted: true}) : null;
			st = state.clone();
			const settled = Pending.settlePending(st, id, key, action.keep ? {kind: 'kept'} : {kind: 'refused', current}, applyAccepted);
			if (settled) return backupSettled(st, settled);
			if (!current) return state;

			// No mark for this save (a reload since, or a later save of this tab holds the row): the record
			// as it is now replaces the refused version this tab shows, or applies under the one acceptance
			// rule (behind a later save it is kept as the latest accepted version)
			const row = st.get(id);
			if (row && row.addedOn === key && !st.pendingSave(id)) {
				applyAccepted(st, current, key);
				return updateStorage(st, [Object.assign({}, current, {staleReplaces: key})]);
			}
			if (applyVersion(st, current)) return updateStorage(st, [current]);
			return st.pendingSave(id) ? st : state;
		}

		case Actions.PARTICIPANT_PENDING_CHECK: {
			// OWA-1018: processQueue read the shared queue. A queued save of this tab whose row has left
			// it (sent by another tab, or discarded) is released; one refused for good there is refused.
			const checked = Pending.checkPendingSaves(state, action.live ?? [], action.refused ?? [], action.readAt ?? 0,
				applyAccepted, Pending.keepsRefusedRows('participant'));
			return checked ? backupSettled(checked.st, checked.write) : state;
		}

		default:
			// No storage update needed
			return state;
	}
}

export default participants;